import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '@/services/auth';
import { setBugtrackerUser } from '@/lib/bugtrackerUser';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);

    useEffect(() => {
        // Initialize authentication state
        const initAuth = async () => {
            const hasToken = await authService.initializeAuth();
            if (hasToken) {
                // If we have a token, fetch user data
                await fetchUserData();
            }
            setIsAuthenticated(hasToken);
            setIsLoading(false);
        };
        
        initAuth();
    }, []);

    const fetchUserData = async () => {
        try {
            const response = await authService.getCurrentUser();
            setUser(response.user);
        } catch (error) {
            console.error('Error fetching user data:', error);
            // If we can't fetch user data, consider the session invalid
            setIsAuthenticated(false);
            setUser(null);
        }
    };

    const clearTradersLocalStorage = () => {
        // Clear all traders-related localStorage keys
        const tradersStorageKeys = [
            'traders_search_results',
            'traders_filters', 
            'traders_search_mode',
            'traders_last_search_time',
            'traders_sort_order',
            'traders_current_page',
            'traders_page_size',
            'traders_auto_execute'
        ];
        
        tradersStorageKeys.forEach(key => {
            try {
                localStorage.removeItem(key);
            } catch (error) {
                console.warn(`Failed to remove localStorage key: ${key}`, error);
            }
        });
        
        console.log('Cleared traders localStorage on login');
    };

    const login = async (username, password) => {
        try {
            const response = await authService.login(username, password);
            setIsAuthenticated(true);
            setUser(response.user);
            
            // Clear traders-related localStorage on successful login
            clearTradersLocalStorage();
            
            // Return success to let the calling component handle navigation
            return { success: true, redirectTo: '/dashboard' };
        } catch (error) {
            throw error;
        }
    };

    const logout = async () => {
        await authService.logout();
        setIsAuthenticated(false);
        setUser(null);
        // Return success to let the calling component handle navigation
        return { success: true, redirectTo: '/login' };
    };

    const updateUser = (updatedUser) => {
        setUser(prev => ({ ...prev, ...updatedUser }));
    };

    useEffect(() => { setBugtrackerUser(user); }, [user]);

    if (isLoading) {
        return <div>Loading...</div>; // You might want to use a proper loading component
    }

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, login, logout, updateUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext; 